/*
 * $Id:: index.js 2021/03/02 11:11 otsolka
 *
 *        . * .
 *      * RRRR  *   Copyright (c) 2012-2021 EUIPO: European Intelectual
 *     .  RR  R  .  Property Organization (trademarks and designs).
 *     *  RRR    *
 *      . RR RR .   ALL RIGHTS RESERVED
 *       *. _ .*
 *
 *  The use and distribution of this software is under the restrictions exposed in 'license.txt'
 */

import React, { useEffect, useState } from 'react';

import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';

import HomepageListTable from './components/HomepageListTable';
import PageTitle from '../../components/PageTitle';
import CustomPagination from '../../components/Pagination/CustomPagination';
import WarningModal from '../../components/WarningModal';
import { t } from '../../providers';
import { me as meSlice } from '../AuthenticatedPage/authenticationSlice';
import { SLICE_NAME } from '../MyApplicationsPage/constants';
import {
    list,
    listTotalCount,
    loading as loadingSlice,
    error as errorSlice,
} from '../MyApplicationsPage/finalizedApplicationsSlice';
import {
    getCookie,
    deleteCookie,
    updateCookie
} from '../../common/utils/localStorageHandler';
import { applicationsParserList } from './utils';


const useStyles = makeStyles((_theme) => ({
    container: {
        width: '100%',
        height: '100%',
        maxWidth: 'calc(100% - 236px)',
        '@media (max-width:1100px)': {
            minWidth: '77%',
            maxWidth: 'unset',
        },
    },
    cardTable: {
        marginRight: '20px',
        '@media (max-width:960px)': {
            marginRight: 'unset',
        },
    },
}));


const ASC = 'ASC';
const DESC = 'DESC';
const DEFAULT_PAGE_SIZE = 5;
const DEFAULT_PAGE_NUMBER = 0;
const DEFAULT_SORT_COLUMN = 'id';
const DEFAULT_SORT_TYPE = DESC;

const HomePage = () => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const loading = useSelector(loadingSlice);
    const error = useSelector(errorSlice);
    const applications = useSelector(list);
    const applicationsCount = useSelector(listTotalCount);
    const me = useSelector(meSlice);
    const { impersonatedUser: readOnly } = me || {};

    const [user, setUser] = useState({ fullName: me.fullName });
    const [applicationList, setapplicationList] = useState([]);
    const [applicationTotalCount, setApplicationTotalCount] = useState(0);
    const [errorDialog, setErrorDialog] = useState({
        title: '',
        visibility: false,
        message: '',
        onConfirmation: null,
        confirmButtonText: null,
        onCancel: null,
        content: null,
    });
    const [sortProps, setSortProps] = useState({sortColumn: DEFAULT_SORT_COLUMN, sortType: DEFAULT_SORT_TYPE});
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [pageNumber, setPageNumber] = useState(DEFAULT_PAGE_NUMBER);

    const handleCloseDialog = () => {
        setErrorDialog({ title: '', visibility: false, message: '', onConfirmation: null, confirmButtonText: null, onCancel: null, content: null });
        dispatch({ type: `${SLICE_NAME}/error`, payload: false });
    };

    const getApplicationReceipt = (appNumber, ipRightType) => {
        dispatch({ type: `${SLICE_NAME}/requestApplicationReceipt`, payload: { appNumber, ipRightType, applicationType: 'undefinedTab' } });
    };

    const setSorting = (sortColumn) => {
        const isSameKey = sortColumn === sortProps.sortColumn;
        const oppositeSort = sortProps.sortType === ASC ? DESC : ASC;
        const sortType = !isSameKey ? ASC : oppositeSort;
        setSortProps({sortColumn, sortType});
    };

    const handleChangePage = (event, newPage) => {
        event.preventDefault();
        if (newPage !== pageNumber) {
            setPageNumber(newPage);
        }
    };

    const handleChangeRowsPerPage = (event) => {
        const _pageSize = event.target.value;
        if (pageSize !== _pageSize) {
            setPageSize(_pageSize);
            setPageNumber(DEFAULT_PAGE_NUMBER);
        }
    };

    const customPagination = (
        <CustomPagination
            defaultPageSize={DEFAULT_PAGE_SIZE}
            totalElements={applicationTotalCount}
            size={pageSize || DEFAULT_PAGE_SIZE}
            page={pageNumber || DEFAULT_PAGE_NUMBER}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            paginationId={'homepage'}
        />
    );

    useEffect(() => {
        const interval = setInterval(() => {
            dispatch({ type: `${SLICE_NAME}/getHomepageApplicationCount`});
        }, 10000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const cookieName = 'accountUpdatedFromRegisters';
        const cookieValue = getCookie(cookieName);
        if(!!cookieValue && cookieValue !== ''){
            setErrorDialog({
                ...errorDialog,
                visibility: true,
                message: t('user.data.updated.from.registers.' + cookieValue),
                onConfirmation: handleCloseDialog,
                confirmButtonText: t('ok'),
            });
        }
        deleteCookie(cookieName);
    }, []);

    useEffect(() => {
        const { fullName, personType, hasApplications } = me;
        setUser({ fullName: fullName || '', userType: personType || ''});
    }, [me]);

    useEffect(() => {
            const params = {
                requestBody: {
                    paginationData: { page: pageNumber, size: pageSize, },
                    sortingData: { sortColumn: DEFAULT_SORT_COLUMN, sortType: DEFAULT_SORT_TYPE },
                    filteringData: {},
                },
            };
            dispatch({ type: `${SLICE_NAME}/requestMyApplications`, payload: { params } });
    }, [dispatch, sortProps, pageSize, pageNumber]);

    useEffect(() => {
        if(applicationTotalCount != applicationsCount){
            //totalCount in DB has changed, request update
            if(DEFAULT_PAGE_NUMBER != pageNumber){
                //enough to chang pageNumber to trigger the request
                setPageNumber(DEFAULT_PAGE_NUMBER);
            } else {
                const params = {
                    requestBody: {
                        paginationData: { page: DEFAULT_PAGE_NUMBER, size: pageSize, },
                        sortingData: { sortColumn: DEFAULT_SORT_COLUMN, sortType: DEFAULT_SORT_TYPE },
                        filteringData: {},
                    },
                };
                dispatch({ type: `${SLICE_NAME}/requestMyApplications`, payload: { params } });
            }
        }
    }, [dispatch, applicationsCount]);

    useEffect(() => {
        const appsList = applicationsParserList(
            applications || [],
            getApplicationReceipt,
            false
        );
        setapplicationList(appsList);
        setApplicationTotalCount(!!applications ? applications.totalResults : 0);
    }, [applications]);

    useEffect(() => {
        if (!!error) {
            let messageModal = t(error);
             setErrorDialog({
                ...errorDialog,
                visibility: true,
                message: messageModal,
                onConfirmation: handleCloseDialog,
                confirmButtonText: t('ok'),
            });
        }
    }, [error]);


    return (
        <div className={classes.container}>
            <PageTitle title={t('dashboard')} details={t('welcome.to.user.area') + ', ' + user.fullName + '. ' + t('refer.online.services')} />
            {/** recentApplications message */}
            <strong>
                <PageTitle details={t('recent.applications')} fontWeight={'600'} />
            </strong>

            {/* data display */}
            <HomepageListTable
                loading={loading}
                setSorting={setSorting}
                sortProps={sortProps}
                applicationsList={applicationList}
                pageSize={pageSize || DEFAULT_PAGE_SIZE}
                pageNumber={pageNumber}
                customPagination={customPagination}
                readOnly={readOnly}
            />

            {/* error dialog */}
            {errorDialog.visibility && (
                <WarningModal
                    title={errorDialog.title}
                    visibility={errorDialog.visibility}
                    message={errorDialog.message}
                    onConfirmation={errorDialog.onConfirmation}
                    confirmButtonText={errorDialog.confirmButtonText}
                    onCancel={errorDialog.onCancel}
                    content={errorDialog.content}
                />
            )}
        </div>
    );
};

export default HomePage;
